import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { rescheduleBooking } from "../../api/bookings";
import type { Booking } from "../../api/bookings";
import type { PendingSyncRetry } from "../../store/bookingsSlice";

interface RescheduleBookingModalProps {
  booking: Booking | null;
  onClose: () => void;
  onSyncFailed?: (payload: PendingSyncRetry) => void;
}

export const RescheduleBookingModal: React.FC<RescheduleBookingModalProps> = ({
  booking,
  onClose,
  onSyncFailed,
}) => {
  const queryClient = useQueryClient();
  const [newDate, setNewDate] = useState<Date | null>(
    booking ? new Date(booking.preferred_date_time) : null
  );

  const mutation = useMutation({
    mutationFn: (isoString: string) =>
      rescheduleBooking(booking!.booking_id, isoString),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      if (!result.calendar_synced && onSyncFailed && booking && newDate) {
        onSyncFailed({
          bookingId: booking.booking_id,
          action: "reschedule",
          newDatetime: newDate.toISOString(),
        });
      }
      onClose();
    },
  });

  const handleConfirm = () => {
    if (newDate) mutation.mutate(newDate.toISOString());
  };

  if (!booking) return null;

  return (
    <Dialog open={Boolean(booking)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reschedule Booking</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rescheduling <strong>{booking.service_name}</strong> for{" "}
          <strong>{booking.customer_name}</strong>
        </Typography>
        <Box sx={{ mt: 1 }}>
          <DateTimePicker
            label="New Date & Time"
            value={newDate}
            onChange={(val) => setNewDate(val)}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                inputProps: { "aria-label": "new date" },
              },
            }}
          />
        </Box>
        {mutation.isError && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            Failed to reschedule booking. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={mutation.isPending || !newDate}
          startIcon={
            mutation.isPending ? <CircularProgress size={16} /> : undefined
          }
        >
          Confirm Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};
